import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid';
import type { CartLine, SelectedOption } from '@/types';

/**
 * Spec §1.7 — "Panier isolé" : one waiter cart per TICKET (session), not per
 * restaurant. Kept entirely separate from the guest `cart` slice
 * (`src/features/cart/cartSlice.ts`) — never reads or writes
 * `cart.byRestaurant`, and persists to `sessionStorage` (device- AND
 * tab-scoped, cleared on tab close) rather than `localStorage` via
 * redux-persist, so it is deliberately absent from `store.ts`'s
 * `persistConfig.whitelist` (['cart', 'user']) — nothing to opt out of here,
 * this slice is simply never registered there.
 *
 * Mirrors `tableSessionSlice.ts`'s self-persistence pattern (try/catch
 * read-through-storage helpers, an explicit `hydrate*` action) rather than
 * `waiterSlice.ts`'s (localStorage, single object) — sessionStorage here,
 * one bucket per sessionUuid.
 */

export const WAITER_CART_STORAGE_PREFIX = 'waiterCart';

export function waiterCartStorageKey(sessionUuid: string): string {
  return `${WAITER_CART_STORAGE_PREFIX}:${sessionUuid}`;
}

/**
 * DF-0224, étendu par DF-0383 — la copie de secours du panier non envoyé.
 *
 * Ce panier meurt avec l'onglet (`sessionStorage`). Un serveur appelé ailleurs,
 * un onglet fermé, une page que le téléphone recycle en arrière-plan, et les
 * plats saisis n'existent plus nulle part — ni en cuisine, ni à l'écran.
 * DF-0224 n'y mettait à l'abri que les plats REPRIS à un client (« Modifier »,
 * SC-953, sort sa ronde de la file de TOUS les appareils). DF-0383 : chez Yuji
 * la S12 a attendu 12 min des plats « saisis à 13:52 » dont il ne restait
 * aucune trace — la copie suit désormais tout le panier.
 *
 * Elle est en `localStorage` : elle survit à l'onglet, sur le même appareil.
 * Chaque onglet y a SA clé, qu'il est seul à écrire — deux onglets qui
 * réécriraient une copie commune s'effaceraient l'un l'autre (mesuré : un
 * plat sur deux perdu au transfert). Ce qu'un onglet perdu a laissé reste
 * donc tant que le serveur n'a pas répondu : saisir un plat ailleurs ne
 * l'efface pas. Rien n'est rechargé en silence (deux onglets rejoueraient la
 * même tournée) : la salle signale la table, `TicketPage` propose de
 * récupérer les plats, c'est un geste.
 *
 * Et ce geste est un transfert, pas une copie : l'onglet qui reprend des plats
 * le note dans sa clé (`taken`), et celui qui les tenait, s'il vit encore, les
 * lâche — sans quoi les deux pourraient les envoyer.
 */
export const WAITER_CART_RECOVERY_PREFIX = 'waiterCartRecovery';

/** La copie d'avant DF-0383 : un tableau nu de lignes reprises, sans onglet ni heure. */
export function waiterCartRecoveryKey(sessionUuid: string): string {
  return `${WAITER_CART_RECOVERY_PREFIX}:${sessionUuid}`;
}

/** La clé d'un onglet dans la copie de secours d'une table. */
export function waiterCartRecoveryTabKey(sessionUuid: string, tabId: string): string {
  return `${waiterCartRecoveryKey(sessionUuid)}:${tabId}`;
}

/**
 * Au-delà, la copie n'est plus proposée. Une table ne se ferme que de main
 * d'homme (SC-667) : sans borne, des plats abandonnés la veille reviendraient
 * sur la tuile le lendemain. 12 h, comme le jeton du serveur et celui du
 * convive — une copie plus vieille que le poste qui l'a saisie est d'un autre
 * service.
 */
export const WAITER_CART_RECOVERY_MAX_AGE_MS = 12 * 60 * 60 * 1000;

/**
 * L'onglet, pour la copie de secours : un identifiant en `sessionStorage`, qui
 * survit donc au rechargement et meurt avec l'onglet — exactement comme le
 * panier. Stockage refusé : le panier ne survit à rien non plus, un
 * identifiant par chargement de page dit la même chose.
 */
const WAITER_CART_TAB_KEY = 'waiterCartTab';

let unstoredTabId: string | null = null;

function currentTabId(): string {
  try {
    const stored = sessionStorage.getItem(WAITER_CART_TAB_KEY);
    if (stored) return stored;
    const created = uuid();
    sessionStorage.setItem(WAITER_CART_TAB_KEY, created);
    return created;
  } catch {
    unstoredTabId ??= uuid();
    return unstoredTabId;
  }
}

/** Ce qu'un onglet a écrit dans sa clé. */
interface RecoverySlot {
  /** Son panier à sa dernière écriture ; `[]` s'il n'y reste que des reprises. */
  lines: CartLine[];
  savedAt: number;
  /** Un envoi de ce panier est parti sans réponse définitive (délai dépassé,
   *  coupure, 5xx) : il est peut-être arrivé en cuisine. Tant que c'est le
   *  cas ces plats ne sont proposés à aucun autre onglet — les renvoyer de
   *  là-bas, sous une autre clé d'idempotence, ferait une seconde tournée. */
  sendPending?: true;
  /** Ligne → quand CET onglet l'a reprise à un autre (ou en a décidé). */
  taken: Record<string, number>;
}

/** Tout ce que l'appareil garde pour une table. */
interface RecoveryCopy {
  slots: Map<string, RecoverySlot>;
  /** La copie d'avant DF-0383 : des lignes reprises, sans onglet ni heure. */
  legacy: CartLine[];
}

function tooOld(at: number): boolean {
  return Date.now() - at > WAITER_CART_RECOVERY_MAX_AGE_MS;
}

/** `null` : rien de lisible ni de récent — la clé peut partir. */
function parseRecoverySlot(raw: string | null): RecoverySlot | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const taken: Record<string, number> = {};
    if (parsed.taken && typeof parsed.taken === 'object') {
      for (const [lineId, at] of Object.entries(parsed.taken)) {
        if (typeof at === 'number' && !tooOld(at)) taken[lineId] = at;
      }
    }
    const fresh = typeof parsed.savedAt === 'number' && !tooOld(parsed.savedAt) && Array.isArray(parsed.lines);
    const lines = fresh ? (parsed.lines as CartLine[]) : [];
    if (lines.length === 0 && Object.keys(taken).length === 0) return null;
    return {
      lines,
      savedAt: fresh ? parsed.savedAt : 0,
      taken,
      ...(fresh && parsed.sendPending === true ? { sendPending: true as const } : {}),
    };
  } catch {
    return null;
  }
}

function parseLegacyRecovery(raw: string | null): CartLine[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CartLine[]) : [];
  } catch {
    return [];
  }
}

/** Les clés que l'appareil garde pour une table : l'ancienne copie, puis une par onglet. */
function recoveryKeysOf(sessionUuid: string): string[] {
  const base = waiterCartRecoveryKey(sessionUuid);
  const keys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && (key === base || key.startsWith(`${base}:`))) keys.push(key);
    }
  } catch {
    // localStorage unavailable
  }
  return keys.sort();
}

function loadRecoveryCopy(sessionUuid: string): RecoveryCopy {
  const base = waiterCartRecoveryKey(sessionUuid);
  const copy: RecoveryCopy = { slots: new Map(), legacy: [] };
  try {
    for (const key of recoveryKeysOf(sessionUuid)) {
      const raw = localStorage.getItem(key);
      if (key === base) {
        copy.legacy = parseLegacyRecovery(raw);
      } else {
        const slot = parseRecoverySlot(raw);
        if (slot) copy.slots.set(key.slice(base.length + 1), slot);
      }
    }
  } catch {
    // localStorage unavailable
  }
  return copy;
}

/**
 * Le texte de tout ce que l'appareil garde pour une table — ce dont un écran
 * observe les changements (`useWaiterCartRecovery`).
 */
export function readWaiterCartRecoveryRaw(sessionUuid: string): string {
  try {
    return recoveryKeysOf(sessionUuid)
      .map((key) => `${key}=${localStorage.getItem(key) ?? ''}`)
      .join('\n');
  } catch {
    return '';
  }
}

/**
 * Ligne → l'onglet qui l'a reprise EN DERNIER : c'est lui qui la tient. Les
 * reprises plus anciennes (A → B, puis B → C) ne comptent plus.
 */
function lastTakers(copy: RecoveryCopy): Map<string, string> {
  const last = new Map<string, { by: string; at: number }>();
  for (const [tabId, slot] of copy.slots) {
    for (const [lineId, at] of Object.entries(slot.taken)) {
      const seen = last.get(lineId);
      if (!seen || at > seen.at || (at === seen.at && tabId > seen.by)) last.set(lineId, { by: tabId, at });
    }
  }
  return new Map([...last].map(([lineId, mark]) => [lineId, mark.by]));
}

export interface WaiterCartRecovery {
  /** Les plats que d'autres onglets de cet appareil ont laissés derrière eux. */
  lines: CartLine[];
  /** L'heure de la dernière saisie parmi eux — `null` pour une copie d'avant DF-0383. */
  savedAt: number | null;
}

/**
 * Ce qu'on peut proposer à cet onglet : les cases des autres, moins celle dont
 * l'envoi est resté sans réponse, moins les lignes qu'un autre onglet leur a
 * reprises depuis.
 */
function offeredToThisTab(copy: RecoveryCopy): WaiterCartRecovery {
  const me = currentTabId();
  const takers = lastTakers(copy);
  const lines = copy.legacy.filter((line) => !takers.has(line.lineId));
  const times: number[] = [];
  for (const [tabId, slot] of copy.slots) {
    if (tabId === me || slot.sendPending) continue;
    const still = slot.lines.filter((line) => (takers.get(line.lineId) ?? tabId) === tabId);
    if (still.length === 0) continue;
    lines.push(...still);
    times.push(slot.savedAt);
  }
  return { lines, savedAt: times.length > 0 ? Math.max(...times) : null };
}

/** Les lignes de ce panier qu'un AUTRE onglet a reprises depuis : elles sont à lui. */
function linesTakenAway(copy: RecoveryCopy): Set<string> {
  const me = currentTabId();
  return new Set([...lastTakers(copy)].flatMap(([lineId, by]) => (by === me ? [] : [lineId])));
}

/** N'écrit QUE la clé de cet onglet ; vide et sans reprise récente, elle part. */
function storeOwnSlot(sessionUuid: string, slot: RecoverySlot): void {
  const key = waiterCartRecoveryTabKey(sessionUuid, currentTabId());
  try {
    if (slot.lines.length === 0 && Object.keys(slot.taken).length === 0) {
      localStorage.removeItem(key);
    } else {
      const { taken, ...rest } = slot;
      localStorage.setItem(key, JSON.stringify(Object.keys(taken).length > 0 ? { ...rest, taken } : rest));
    }
  } catch {
    // localStorage unavailable (private mode, storage blocked, quota)
  }
}

/** L'ancienne copie n'a plus d'onglet pour la tenir à jour : ce qu'on lui reprend en sort. */
function dropFromLegacy(sessionUuid: string, copy: RecoveryCopy, gone: Set<string>): void {
  if (!copy.legacy.some((line) => gone.has(line.lineId))) return;
  const kept = copy.legacy.filter((line) => !gone.has(line.lineId));
  try {
    if (kept.length === 0) localStorage.removeItem(waiterCartRecoveryKey(sessionUuid));
    else localStorage.setItem(waiterCartRecoveryKey(sessionUuid), JSON.stringify(kept));
  } catch {
    // localStorage unavailable
  }
}

/**
 * La case de cet onglet suit son panier. `answeredIds` : les lignes dont cet
 * onglet vient de décider — reprises à un autre (« Récupérer », même fondues
 * dans une ligne qu'il tenait déjà), retirées, envoyées, vidées. Là où un
 * autre onglet les affiche encore, celui-ci note qu'il les a reprises : elles
 * ne seront plus proposées, et leur ancien onglet les lâchera.
 */
function persistRecovery(sessionUuid: string, lines: CartLine[], answeredIds: string[] = []): void {
  const me = currentTabId();
  const copy = loadRecoveryCopy(sessionUuid);
  const takers = lastTakers(copy);
  const answered = new Set(answeredIds);
  const taken = { ...(copy.slots.get(me)?.taken ?? {}) };
  const now = Date.now();
  // Une ligne simplement encore dans ce panier ne se reprend pas à l'onglet qui
  // vient de la lui prendre : seul un geste (`answeredIds`) le fait.
  const takes = (lineId: string) =>
    answered.has(lineId) || (lines.some((line) => line.lineId === lineId) && (takers.get(lineId) ?? me) === me);
  for (const [tabId, slot] of copy.slots) {
    if (tabId === me) continue;
    for (const line of slot.lines) if (takes(line.lineId)) taken[line.lineId] = now;
  }
  const fromLegacy = new Set(copy.legacy.flatMap((line) => (takes(line.lineId) ? [line.lineId] : [])));
  for (const lineId of fromLegacy) taken[lineId] = now;
  dropFromLegacy(sessionUuid, copy, fromLegacy);
  const sendPending = lines.length > 0 ? copy.slots.get(me)?.sendPending : undefined;
  storeOwnSlot(sessionUuid, { lines, savedAt: now, taken, ...(sendPending ? { sendPending } : {}) });
}

function removeRecoveryFromStorage(sessionUuid: string): void {
  try {
    for (const key of recoveryKeysOf(sessionUuid)) localStorage.removeItem(key);
  } catch {
    // localStorage unavailable
  }
}

/** Ce que d'autres onglets de cet appareil ont laissé pour cette table. */
export function readWaiterCartRecoveryView(sessionUuid: string): WaiterCartRecovery {
  return offeredToThisTab(loadRecoveryCopy(sessionUuid));
}

/**
 * Les plats que d'autres onglets de cet appareil ont laissés derrière eux, ou
 * `[]`. Lu par `TicketPage` pour proposer de les récupérer — jamais par le
 * reducer d'hydratation, qui ne doit rien ressusciter tout seul.
 */
export function readWaiterCartRecovery(sessionUuid: string): CartLine[] {
  return readWaiterCartRecoveryView(sessionUuid).lines;
}

/** L'heure de la dernière saisie laissée par un autre onglet — ce que le ticket
 *  affiche pour que le serveur sache de quels plats on lui parle. */
export function readWaiterCartRecoverySavedAt(sessionUuid: string): number | null {
  return readWaiterCartRecoveryView(sessionUuid).savedAt;
}

/**
 * « Supprimer » sur la proposition du ticket : les plats saisis à la main que
 * la fenêtre a NOMMÉS sortent de la copie — pas ceux qu'un onglet y aurait
 * ajoutés entre-temps. Les plats REPRIS à un client y restent : personne
 * d'autre ne les a (DF-0224), ils ne partent que récupérés.
 */
export function dropTypedWaiterCartRecovery(sessionUuid: string, lineIds: string[]): void {
  const me = currentTabId();
  const copy = loadRecoveryCopy(sessionUuid);
  const named = new Set(lineIds);
  const mine = copy.slots.get(me) ?? { lines: [], savedAt: Date.now(), taken: {} };
  const taken = { ...mine.taken };
  for (const [tabId, slot] of copy.slots) {
    if (tabId === me) continue;
    for (const line of slot.lines) {
      if (named.has(line.lineId) && line.takenOver !== true) taken[line.lineId] = Date.now();
    }
  }
  storeOwnSlot(sessionUuid, { ...mine, taken });
}

/**
 * L'envoi d'une tournée part (`pending`), ou vient d'être refusé net par le
 * serveur (`false` : rien n'est arrivé en cuisine, le panier redevient
 * récupérable). Réussi, le panier est vidé et la case part avec lui.
 */
export function markWaiterSendPending(sessionUuid: string, pending: boolean): void {
  const slot = loadRecoveryCopy(sessionUuid).slots.get(currentTabId());
  if (!slot || slot.lines.length === 0 || (slot.sendPending === true) === pending) return;
  const { sendPending: _was, ...rest } = slot;
  storeOwnSlot(sessionUuid, pending ? { ...rest, sendPending: true } : rest);
}

/**
 * DF-0383 — ce que CET appareil a saisi pour une table et pas envoyé, en
 * unités : le panier de l'onglet, plus ce que d'autres onglets ont laissé dans
 * la copie de secours. Lu par la salle, où le store ne sait rien — il n'est
 * hydraté que par le ticket qu'on ouvre.
 */
export function readUnsentWaiterQty(sessionUuid: string): number {
  const copy = loadRecoveryCopy(sessionUuid);
  const takenAway = linesTakenAway(copy);
  const inTab = loadSessionFromStorage(sessionUuid).filter((line) => !takenAway.has(line.lineId));
  const inTabIds = new Set(inTab.map((line) => line.lineId));
  const left = offeredToThisTab(copy).lines.filter((line) => !inTabIds.has(line.lineId));
  return [...inTab, ...left].reduce((sum, line) => sum + line.qty, 0);
}

/**
 * La table dont `key` fait partie de la copie de secours, si cet onglet y
 * tient un panier — l'événement `storage` d'un autre onglet qui vient
 * peut-être de lui reprendre des plats. `null` pour toute autre clé.
 */
export function waiterCartSessionOfRecoveryKey(key: string | null): string | null {
  const prefix = `${WAITER_CART_RECOVERY_PREFIX}:`;
  if (!key || !key.startsWith(prefix)) return null;
  const sessionUuid = key.slice(prefix.length).split(':')[0];
  return loadSessionFromStorage(sessionUuid).length > 0 ? sessionUuid : null;
}

/**
 * Les clés dont plus rien n'est lisible ni récent sortent du stockage : sans
 * cela, la copie d'une table fermée depuis un autre appareil y resterait pour
 * toujours. L'ancienne copie, sans heure, n'est pas jugée : elle part quand on
 * la reprend ou quand la table se ferme.
 */
export function sweepWaiterCartRecovery(): void {
  try {
    const prefix = `${WAITER_CART_RECOVERY_PREFIX}:`;
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
      const raw = localStorage.getItem(key);
      const isLegacy = !key.slice(prefix.length).includes(':');
      if (isLegacy ? parseLegacyRecovery(raw).length === 0 : parseRecoverySlot(raw) === null) stale.push(key);
    }
    for (const key of stale) localStorage.removeItem(key);
  } catch {
    // localStorage unavailable
  }
}

interface WaiterCartState {
  bySession: Record<string, CartLine[]>;
  /** sessionUuid → itemId → lineId of "the last plate added" of that product:
   *  the line the last row `+`, modal Valider or cart `+` landed on. The row's
   *  blue button edits THAT line (old-app rule, SC-842) — positional "last
   *  line of the product" is wrong as soon as a `+` merges into an earlier
   *  plain line sitting above a noted one. Deliberately NOT persisted: after
   *  a reload `resolveLastLine` falls back to position. */
  lastTouched: Record<string, Record<string, string>>;
}

const initialState: WaiterCartState = {
  bySession: {},
  lastTouched: {},
};

function touch(state: WaiterCartState, sessionUuid: string, itemId: string, lineId: string): void {
  if (!state.lastTouched[sessionUuid]) state.lastTouched[sessionUuid] = {};
  state.lastTouched[sessionUuid][itemId] = lineId;
}

function untouch(state: WaiterCartState, sessionUuid: string, lineId: string): void {
  const map = state.lastTouched[sessionUuid];
  if (!map) return;
  for (const itemId of Object.keys(map)) {
    if (map[itemId] === lineId) delete map[itemId];
  }
}

function persistSession(sessionUuid: string, lines: CartLine[], answeredIds?: string[]): void {
  try {
    sessionStorage.setItem(waiterCartStorageKey(sessionUuid), JSON.stringify(lines));
  } catch {
    // sessionStorage unavailable (e.g. private mode with storage blocked)
  }
  // DF-0224 — le seul point de passage de toute écriture du panier : la copie
  // de secours suit exactement le panier, y compris quand le serveur en retire
  // une ligne (elle sort alors des deux).
  persistRecovery(sessionUuid, lines, answeredIds);
}

function removeSessionFromStorage(sessionUuid: string, clearedIds: string[]): void {
  try {
    sessionStorage.removeItem(waiterCartStorageKey(sessionUuid));
  } catch {
    // sessionStorage unavailable
  }
  // Vider est un geste délibéré (tournée envoyée, « Vider et quitter ») : il
  // emporte la copie de secours de CE panier avec lui, sinon la proposition de
  // récupérer reviendrait indéfiniment. Le garde de sortie prévient quand le
  // panier contient des plats repris — DF-0224. Ce qu'un autre onglet a laissé
  // n'est pas ce panier-ci : cela attend toujours sa réponse (DF-0383).
  persistRecovery(sessionUuid, [], clearedIds);
}

function loadSessionFromStorage(sessionUuid: string): CartLine[] {
  try {
    const raw = sessionStorage.getItem(waiterCartStorageKey(sessionUuid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CartLine[]) : [];
  } catch {
    return [];
  }
}

/**
 * Stable key over the merge-relevant fields of a waiter cart line — same
 * shape as `cartSlice.ts`'s (unexported) `lineMergeKey`, duplicated rather
 * than imported: the guest cart slice is out of scope for this feature and
 * must not be touched.
 */
function waiterLineMergeKey(line: CartLine): string {
  const opts = line.selectedOptions
    .map((g: SelectedOption) => ({
      groupId: g.groupId,
      options: [...g.options]
        .map((o) => ({ id: o.id, qty: o.qty }))
        .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)),
    }))
    .sort((a, b) => a.groupId.localeCompare(b.groupId));
  return JSON.stringify({ itemId: line.itemId, opts, unitPrice: line.unitPrice, note: line.note ?? '' });
}

interface BySessionLineAction {
  sessionUuid: string;
  lineId: string;
}

const waiterCartSlice = createSlice({
  name: 'waiterCart',
  initialState,
  reducers: {
    hydrateWaiterCart(state, action: PayloadAction<string>) {
      const sessionUuid = action.payload;
      const stored = loadSessionFromStorage(sessionUuid);
      // DF-0383 — des plats qu'un autre onglet a repris depuis (« Récupérer »)
      // sont à lui : ce panier les lâche, sinon les deux les enverraient.
      const copy = loadRecoveryCopy(sessionUuid);
      const takenAway = linesTakenAway(copy);
      const lines = stored.filter((line) => !takenAway.has(line.lineId));
      state.bySession[sessionUuid] = lines;
      // Un panier sans case à lui — rechargé juste après la mise à jour — prend
      // la sienne ici : sans elle, ni la reprise de l'ancienne copie ni l'envoi
      // en suspens ne le couvriraient. Une seule fois : écrire à chaque
      // hydratation ferait se répondre sans fin deux onglets ouverts.
      const unslotted = lines.length > 0 && !copy.slots.has(currentTabId());
      if (lines.length !== stored.length || unslotted) persistSession(sessionUuid, lines);
    },
    addWaiterLine(state, action: PayloadAction<{ sessionUuid: string; line: CartLine }>) {
      const { sessionUuid, line } = action.payload;
      if (!state.bySession[sessionUuid]) state.bySession[sessionUuid] = [];
      const bucket = state.bySession[sessionUuid];
      const incomingKey = waiterLineMergeKey(line);
      const existing = bucket.find((l) => waiterLineMergeKey(l) === incomingKey);
      if (existing) {
        existing.qty += line.qty;
        // DF-0224 — une ligne reprise fondue dans une ligne saisie à la main
        // reste reprise : la copie de secours ne doit pas perdre ces plats-là.
        if (line.takenOver) existing.takenOver = true;
        touch(state, sessionUuid, line.itemId, existing.lineId);
      } else {
        bucket.push(line);
        touch(state, sessionUuid, line.itemId, line.lineId);
      }
      persistSession(sessionUuid, bucket, [line.lineId]);
    },
    setWaiterLineQty(state, action: PayloadAction<BySessionLineAction & { qty: number }>) {
      const { sessionUuid, lineId, qty } = action.payload;
      const bucket = state.bySession[sessionUuid];
      if (!bucket) return;
      if (qty <= 0) {
        state.bySession[sessionUuid] = bucket.filter((l) => l.lineId !== lineId);
        untouch(state, sessionUuid, lineId);
      } else {
        const line = bucket.find((l) => l.lineId === lineId);
        if (line) {
          // A cart `+` is an add too — the plate it adds is the last one.
          if (qty > line.qty) touch(state, sessionUuid, line.itemId, lineId);
          line.qty = qty;
        }
      }
      persistSession(sessionUuid, state.bySession[sessionUuid], qty <= 0 ? [lineId] : undefined);
    },
    removeWaiterLine(state, action: PayloadAction<BySessionLineAction>) {
      const { sessionUuid, lineId } = action.payload;
      const bucket = state.bySession[sessionUuid];
      if (!bucket) return;
      state.bySession[sessionUuid] = bucket.filter((l) => l.lineId !== lineId);
      untouch(state, sessionUuid, lineId);
      persistSession(sessionUuid, state.bySession[sessionUuid], [lineId]);
    },
    /**
     * Edits the options/comment of an existing line (the options-modal
     * "options" round-trip, spec §1.7: "le bouton « options » édite la
     * dernière ligne"). If the edited line now collides with another line
     * of the same product/options/comment, they merge (qty summed) and the
     * edited line is dropped — same collision semantics as `addWaiterLine`.
     *
     * `applyTo: 'lastPlate'` (the row's blue button, SC-842) edits ONE unit
     * only: with qty > 1 the other units keep their options/comment on the
     * original line and the edited unit becomes its own line, appended under
     * `newLineId` — « × 2 » plain + « × 1 sans oignons », like the old app
     * where every optioned `+` was its own line. `'line'` (default — the
     * cart's name tap) edits every unit of the line.
     */
    replaceWaiterLine(
      state,
      action: PayloadAction<
        BySessionLineAction & {
          selectedOptions: SelectedOption[];
          note?: string;
          optionNames?: string[];
          /** Recomputed by the caller (`computeUnitPrice`) — a pricier option must
           *  never keep the old price; omitted = unchanged (comment-only edit). */
          unitPrice?: number;
          applyTo?: 'line' | 'lastPlate';
          /** Required for the split (`applyTo: 'lastPlate'` on a qty > 1 line);
           *  generated by the caller — reducers stay pure. */
          newLineId?: string;
        }
      >,
    ) {
      const { sessionUuid, lineId, selectedOptions, note, optionNames, unitPrice, applyTo, newLineId } =
        action.payload;
      const bucket = state.bySession[sessionUuid];
      if (!bucket) return;
      const idx = bucket.findIndex((l) => l.lineId === lineId);
      if (idx === -1) return;
      const original = bucket[idx];
      const itemId = original.itemId;
      const updated: CartLine = {
        ...original,
        selectedOptions,
        note,
        optionNames: optionNames ?? original.optionNames,
        unitPrice: unitPrice ?? original.unitPrice,
      };
      const updatedKey = waiterLineMergeKey(updated);
      if (applyTo === 'lastPlate' && original.qty > 1 && newLineId) {
        if (updatedKey === waiterLineMergeKey(original)) return; // nothing changed
        original.qty -= 1;
        const collisionIdx = bucket.findIndex((l) => waiterLineMergeKey(l) === updatedKey);
        if (collisionIdx !== -1) {
          bucket[collisionIdx].qty += 1;
          touch(state, sessionUuid, itemId, bucket[collisionIdx].lineId);
        } else {
          bucket.push({ ...updated, lineId: newLineId, qty: 1 });
          touch(state, sessionUuid, itemId, newLineId);
        }
        persistSession(sessionUuid, bucket);
        return;
      }
      const collisionIdx = bucket.findIndex((l, i) => i !== idx && waiterLineMergeKey(l) === updatedKey);
      if (collisionIdx !== -1) {
        bucket[collisionIdx].qty += updated.qty;
        const survivorId = bucket[collisionIdx].lineId;
        state.bySession[sessionUuid] = bucket.filter((_, i) => i !== idx);
        touch(state, sessionUuid, itemId, survivorId);
      } else {
        bucket[idx] = updated;
        touch(state, sessionUuid, itemId, lineId);
      }
      persistSession(sessionUuid, state.bySession[sessionUuid], [lineId]);
    },
    clearWaiterCartSession(state, action: PayloadAction<string>) {
      const sessionUuid = action.payload;
      const clearedIds = (state.bySession[sessionUuid] ?? []).map((line) => line.lineId);
      state.bySession[sessionUuid] = [];
      delete state.lastTouched[sessionUuid];
      removeSessionFromStorage(sessionUuid, clearedIds);
    },
    /**
     * La table est fermée (service terminé, annulée, fermée depuis un autre
     * appareil) : plus rien ne pourra y être envoyé, la copie de secours part
     * en entier — ce panier-ci et ce que d'autres onglets y avaient laissé.
     */
    forgetWaiterCartSession(state, action: PayloadAction<string>) {
      const sessionUuid = action.payload;
      state.bySession[sessionUuid] = [];
      delete state.lastTouched[sessionUuid];
      removeSessionFromStorage(sessionUuid, []);
      removeRecoveryFromStorage(sessionUuid);
    },
    /**
     * DF-0120 — apply a fresh unit price from `repriceCartLines`
     * (the send-time menu refetch) before a round is posted. Sets the price
     * in place only: unlike `replaceWaiterLine`, no merge/collision handling
     * — a price correction never changes which line a set of options/qty
     * belongs to, so there is nothing to merge into.
     */
    updateWaiterLineUnitPrice(state, action: PayloadAction<BySessionLineAction & { unitPrice: number }>) {
      const { sessionUuid, lineId, unitPrice } = action.payload;
      const bucket = state.bySession[sessionUuid];
      if (!bucket) return;
      const line = bucket.find((l) => l.lineId === lineId);
      if (line) line.unitPrice = unitPrice;
      persistSession(sessionUuid, bucket);
    },
  },
});

export const {
  hydrateWaiterCart,
  addWaiterLine,
  setWaiterLineQty,
  removeWaiterLine,
  replaceWaiterLine,
  clearWaiterCartSession,
  forgetWaiterCartSession,
  updateWaiterLineUnitPrice,
} = waiterCartSlice.actions;

export default waiterCartSlice.reducer;
export const waiterCartReducer = waiterCartSlice.reducer;

// Selectors
type StateWithWaiterCart = { waiterCart: WaiterCartState };

const NO_WAITER_LINES: CartLine[] = [];
const NO_LAST_TOUCHED: Record<string, string> = {};

export const selectWaiterCartLines =
  (sessionUuid: string) =>
  (state: StateWithWaiterCart): CartLine[] =>
    state.waiterCart.bySession[sessionUuid] ?? NO_WAITER_LINES;

export const selectWaiterLastTouched =
  (sessionUuid: string) =>
  (state: StateWithWaiterCart): Record<string, string> =>
    state.waiterCart.lastTouched[sessionUuid] ?? NO_LAST_TOUCHED;

/**
 * The line the row's blue button targets — "the last plate added" of that
 * product (old-app `cart.findLast`, SC-842): the marked line when it still
 * exists, else the product's last line by position (a reloaded tab has no
 * marker), else null.
 */
export function resolveLastLine(
  lines: CartLine[],
  lastTouched: Record<string, string>,
  itemId: string,
): CartLine | null {
  const marked = lastTouched[itemId];
  if (marked) {
    const hit = lines.find((l) => l.lineId === marked && l.itemId === itemId);
    if (hit) return hit;
  }
  const matches = lines.filter((l) => l.itemId === itemId);
  return matches[matches.length - 1] ?? null;
}

/** Total units — the cart badge sums `qty` across every line, like the guest
 *  `selectCartCount`. The old app counted distinct lines and the parity spec
 *  copied that; SC-854 reverses it: five of the same dish must read `5`. */
export const selectWaiterCartQty =
  (sessionUuid: string) =>
  (state: StateWithWaiterCart): number =>
    selectWaiterCartLines(sessionUuid)(state).reduce((acc, l) => acc + l.qty, 0);

/** Integer cents. `unitPrice` is passed in already resolved (per-mode price +
 *  option supplements, see `computeUnitPrice`/`resolveModePrice`). No
 *  promotion is applied here — a takeaway note's promotion is picked in
 *  `TicketConfirmRound` (SC-808), which uses this very figure as its base. */
export const selectWaiterCartTotal =
  (sessionUuid: string) =>
  (state: StateWithWaiterCart): number =>
    selectWaiterCartLines(sessionUuid)(state).reduce((acc, l) => acc + l.unitPrice * l.qty, 0);
